import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { orpc } from '@/lib/orpc-client'
import { reportServerBuildId } from '@/lib/app-version'
import { useAuthState } from '@/lib/auth-state'
import {
  initialNotificationState,
  notificationReducer,
  type NotificationBadge,
  type NotificationState,
} from './notification-state'

export type { NotificationBadge } from './notification-state'

// --- Types ---

export interface NotificationItem {
  id: string
  category: 'notification' | 'activity'
  type: string
  title: string
  body: string | null
  actorId: string | null
  actorName: string | null
  projectId: string
  projectName: string | null
  resourceType: string | null
  resourceId: string | null
  /** Position inside the resource (document block id) — see notification-panel.tsx. */
  anchor: string | null
  isRead: boolean
  /** First creation of the row. */
  createdAt: string
  /** The newest event in the row (a coalesced re-mention/reply refreshes it) — list order and shown time. */
  notifiedAt: string
}

interface NotificationContextValue {
  items: NotificationItem[]
  badge: NotificationBadge
  connected: boolean
  markRead: (notificationId: string) => Promise<void>
  markAllRead: (category?: 'notification' | 'activity') => Promise<void>
  loadMore: () => Promise<void>
  hasMore: boolean
}

// --- Context ---

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return ctx
}

// --- Provider ---

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { status } = useAuthState()
  const isAuthenticated = status === 'authenticated'

  if (!isAuthenticated) {
    return (
      <NotificationContext.Provider
        value={{
          items: [],
          badge: { unreadCount: 0, hasUnseenActivity: false },
          connected: false,
          markRead: async () => {},
          markAllRead: async () => {},
          loadMore: async () => {},
          hasMore: false,
        }}
      >
        {children}
      </NotificationContext.Provider>
    )
  }

  return <NotificationProviderInner>{children}</NotificationProviderInner>
}

function NotificationProviderInner({ children }: { children: ReactNode }) {
  // Items, badge and cursor live in one reducer: every update — a stream
  // row, a loadMore page, mark-read — is a function of the previous state,
  // so none can overwrite another queued in the same tick (notification-state.ts).
  const [state, dispatch] = useReducer(
    notificationReducer<NotificationItem>,
    initialNotificationState as NotificationState<NotificationItem>
  )
  const { items, badge, cursor, hasMore } = state
  const [connected, setConnected] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)

  // Load initial data (items + badge) via API on mount
  useEffect(() => {
    orpc.notifications
      .list({ limit: 30 })
      .then((result) => dispatch({ type: 'loaded', ...result }))
      .catch((err) => console.error('[Notifications] Failed to load initial items:', err))

    orpc.notifications
      .badge({})
      .then((badge) => dispatch({ type: 'badge', badge }))
      .catch((err) => console.error('[Notifications] Failed to load initial badge:', err))
  }, [])

  // Connect to SSE stream for real-time updates
  useEffect(() => {
    const url = buildSseUrl()
    const es = new EventSource(url, { withCredentials: true })
    eventSourceRef.current = es

    // A row (new, coalesced, or read elsewhere) upserts into the list and
    // bumps the badge optimistically — the server's badge event follows.
    const onRow = (e: MessageEvent) => {
      try {
        const item = JSON.parse(e.data) as NotificationItem
        dispatch({ type: 'stream', item })
      } catch {
        // Ignore malformed events
      }
    }
    es.addEventListener('notification', onRow)
    es.addEventListener('activity', onRow)

    es.addEventListener('version', (e) => {
      try {
        const data = JSON.parse(e.data) as { buildId?: string }
        if (typeof data.buildId === 'string') reportServerBuildId(data.buildId)
      } catch {
        // Ignore malformed events
      }
    })

    es.addEventListener('badge', (e) => {
      try {
        const badge = JSON.parse(e.data) as NotificationBadge
        dispatch({ type: 'badge', badge })
      } catch {
        // Ignore malformed events
      }
    })

    es.onopen = () => setConnected(true)
    es.onerror = () => setConnected(false)

    return () => {
      es.close()
      eventSourceRef.current = null
      setConnected(false)
    }
  }, [])

  const markRead = useCallback(async (notificationId: string) => {
    await orpc.notifications.markRead({ notificationId })
    dispatch({ type: 'markRead', id: notificationId })
    // Refresh badge from server
    dispatch({ type: 'badge', badge: await orpc.notifications.badge({}) })
  }, [])

  const markAllRead = useCallback(async (category?: 'notification' | 'activity') => {
    await orpc.notifications.markAllRead({ category })
    dispatch({ type: 'markAllRead', category })
    dispatch({ type: 'badge', badge: await orpc.notifications.badge({}) })
  }, [])

  const loadMore = useCallback(async () => {
    if (!cursor || !hasMore) return
    const result = await orpc.notifications.list({ cursor, limit: 30 })
    dispatch({ type: 'append', ...result })
  }, [cursor, hasMore])

  return (
    <NotificationContext.Provider
      value={{ items, badge, connected, markRead, markAllRead, loadMore, hasMore }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

// --- Helpers ---

function buildSseUrl(): string {
  return `${window.location.origin}/notifications/stream`
}
